import { Link } from 'react-router-dom';
import { Button, Reveal } from '../ui';
import { useDeferredMount } from '../../lib/useDeferredMount';
import { siteImage } from './homeImages';

/**
 * C7 intro split: practice name (light H2) + two short paragraphs of existing
 * VE copy with pink inline links + outline button on the left; a two-column
 * masonry collage of five of our own photos on the right (offset heights,
 * 10px radius, 12px gaps). Phones: collage first as a 2-column grid.
 */
// Tiles are ~45vw on phones (about 176 CSS px, so ~530 device px at 3x): the
// w360/w640 steps keep them sharp without pulling oversized files after the
// collage mounts on idle/scroll.
const COLLAGE_WIDTHS = [360, 480, 640, 960];

const COLLAGE = [
  { ...siteImage('zach-pick-71e58acf93-tall', 960, 1440, COLLAGE_WIDTHS), alt: 'Dr. Zach Conner smiling as he gives a baby a gentle adjustment on the table', ratio: 'tall' },
  { ...siteImage('saylor26-john-saylor-front-desk-a', 960, 640, COLLAGE_WIDTHS), alt: 'Dr. John Johr and Dr. Anna Saylor at our front desk', ratio: 'wide' },
  { ...siteImage('saylor26-activator-adjustment-chiroland', 960, 640, [360, 480, 768, 960]), alt: 'Dr. Anna Saylor smiling as she gives a patient a gentle adjustment in front of our painted jungle wall', ratio: 'wide' },
  { ...siteImage('saylor26-newborn-jungle-backdrop', 960, 960, COLLAGE_WIDTHS), alt: 'Dr. Anna Saylor smiling with a newborn in front of our jungle mural', ratio: 'tall' },
  { ...siteImage('saylor26-seated-infant-adjustment-b', 960, 1280, COLLAGE_WIDTHS), alt: 'Dr. Anna Saylor cradling an infant during a gentle visit', ratio: 'wide' },
];

const SIZES = '(max-width: 767px) 45vw, (max-width: 1023px) 40vw, 260px';

// On phones this collage sits right under the hero, inside the browser's lazy
// loading distance, so its five photos (~180 KB) were fetched while the hero
// photo was still arriving (home mobile LCP 4+ s on a slow connection). The
// tiles keep their reserved size and show a blank pixel until the page is idle
// or the visitor starts to scroll, then the real photos load.
const BLANK_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

export default function HomeIntro() {
  const photosReady = useDeferredMount(1500);
  return (
    <section className="ve-section ve-home-intro" aria-labelledby="home-intro-title">
      <div className="ve-container ve-home-intro-grid">
        <div className="ve-home-intro-text ve-prose">
          <span className="ve-eyebrow">Royal Oak family chiropractic since 1997</span>
          <h2 id="home-intro-title">Van Every Family Chiropractic Center</h2>
          <p className="ve-lead">
            Newborns, busy kids, expecting moms, and grown-ups all come through our doors on
            Rochester Road. Our <Link to="/meet-the-doctors/">three doctors</Link> bring over 60 years
            of combined experience and a gentle, nervous-system focused approach: we measure first
            with <Link to="/our-techniques/">INSiGHT scanning</Link>, then adjust with light, precise
            contacts. No cracking or twisting.
          </p>
          <p>
            Dr. Anna Saylor opened the practice in 1997. Today three doctors share her focus on
            families. All three care for patients of every age and use the full range of gentle,
            nervous-system focused techniques available in our office.
          </p>
          <div className="ve-btn-row">
            <Button to="/about-us/" variant="outline">
              Get to know our office
            </Button>
          </div>
        </div>
        <Reveal direction="right" className="ve-home-collage-wrap">
          <ul className="ve-home-collage" aria-label="Photos from our Royal Oak office">
            {COLLAGE.map((img) => (
              <li key={img.src} className={`ve-home-collage-item ve-home-collage-item--${img.ratio}`}>
                <img
                  src={photosReady ? img.src : BLANK_PIXEL}
                  srcSet={photosReady ? img.srcSet : undefined}
                  sizes={photosReady ? SIZES : undefined}
                  alt={img.alt}
                  width={img.width}
                  height={img.height}
                  loading="lazy"
                  decoding="async"
                />
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
}
